import { useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";

import SectionLabel from "../../../app/universalComponents/SectionLabel";
import ErrorState from "../../../app/universalComponents/ErrorState";
import Input from "../../../app/universalComponents/Input";
import Dropdown from "../../../app/universalComponents/Dropdown";
import Button from "../../../app/universalComponents/Button";
import OrderRow from "./OrderRow";
import { useEventOrders } from "../dashboardApi";

const defaultFilters = { search: "", status: "all", sort: "desc" };

function getScrollParent(element) {
  let parent = element?.parentElement;

  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;

    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return window;
}

function getScrollTop(scrollParent) {
  return scrollParent === window
    ? window.scrollY
    : scrollParent.scrollTop;
}

function getDistanceFromBottom(scrollParent) {
  if (scrollParent === window) {
    return (
      document.documentElement.scrollHeight -
      window.innerHeight -
      window.scrollY
    );
  }

  return (
    scrollParent.scrollHeight -
    scrollParent.scrollTop -
    scrollParent.clientHeight
  );
}

export default function OrdersTab() {
  const { activeEvent } = useOutletContext();

  const tableRef = useRef(null);
  const scrollParentRef = useRef(null);
  const loadLockedRef = useRef(false);
  const previousScrollTopRef = useRef(0);
  const previousEventIdRef = useRef(activeEvent?.id);

  const limit = 25;

  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const [form, setForm] = useState(defaultFilters);

  const [filters, setFilters] = useState({
    ...defaultFilters,
    requestKey: 0,
  });

  const ordersQuery = useEventOrders(activeEvent?.id, {
    page,
    limit,
    search: filters.search,
    status: filters.status === "all" ? [] : [filters.status],
    sort: filters.sort,
    requestKey: filters.requestKey,
  });

  const scrollToTop = useCallback(() => {
    const scrollParent = scrollParentRef.current;

    if (!scrollParent) {
      return;
    }

    scrollParent.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  const resetPagination = useCallback(() => {
    setPage(1);
    setOrders([]);
    setHasMore(true);

    loadLockedRef.current = false;
    previousScrollTopRef.current = 0;

    scrollToTop();
  }, [scrollToTop]);

  useEffect(() => {
    const responsePage = ordersQuery.data?.page;
    const newOrders = ordersQuery.data?.orders;

    if (
      !Array.isArray(newOrders) ||
      Number(responsePage) !== page
    ) {
      return;
    }

    setOrders((currentOrders) => {
      if (page === 1) {
        return newOrders;
      }

      const existingIds = new Set(
        currentOrders.map((order) => order.id),
      );

      return [
        ...currentOrders,
        ...newOrders.filter(
          (order) => !existingIds.has(order.id),
        ),
      ];
    });

    setHasMore(newOrders.length === limit);
    loadLockedRef.current = false;
  }, [ordersQuery.data, page]);

  useEffect(() => {
    if (previousEventIdRef.current === activeEvent?.id) {
      return;
    }

    previousEventIdRef.current = activeEvent?.id;

    setForm(defaultFilters);
    resetPagination();

    setFilters((currentFilters) => ({
      ...defaultFilters,
      requestKey: currentFilters.requestKey + 1,
    }));
  }, [activeEvent?.id, resetPagination]);

  useEffect(() => {
    const scrollParent = getScrollParent(tableRef.current);

    scrollParentRef.current = scrollParent;
    previousScrollTopRef.current = getScrollTop(scrollParent);

    function handleScroll() {
      const currentScrollTop = getScrollTop(scrollParent);

      const isScrollingDown =
        currentScrollTop > previousScrollTopRef.current;

      previousScrollTopRef.current = currentScrollTop;

      if (
        !isScrollingDown ||
        getDistanceFromBottom(scrollParent) > 400 ||
        loadLockedRef.current ||
        !hasMore ||
        ordersQuery.isFetching
      ) {
        return;
      }

      loadLockedRef.current = true;

      setPage((currentPage) => currentPage + 1);
    }

    scrollParent.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollParent.removeEventListener("scroll", handleScroll);
    };
  }, [
    activeEvent?.id,
    hasMore,
    ordersQuery.isFetching,
  ]);

  function updateForm(name, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  }

  function handleSubmit(event) {
    event.preventDefault();

    resetPagination();

    setFilters((currentFilters) => ({
      search: form.search.trim(),
      status: form.status,
      sort: form.sort,
      requestKey: currentFilters.requestKey + 1,
    }));
  }

  function handleClear() {
    setForm(defaultFilters);
    resetPagination();

    setFilters((currentFilters) => ({
      ...defaultFilters,
      requestKey: currentFilters.requestKey + 1,
    }));
  }

  const isLoadingFirstPage =
    page === 1 &&
    orders.length === 0 &&
    ordersQuery.isFetching;

  const showEmptyState =
    !isLoadingFirstPage &&
    !ordersQuery.isError &&
    orders.length === 0;

  if (ordersQuery.isError) {
    return (
      <ErrorState>
        {ordersQuery.error.message}
      </ErrorState>
    );
  }

  if (
    ["draft", "pending_approval"].includes(
      activeEvent.status,
    )
  ) {
    return (
      <ErrorState>
        No orders yet. Orders will appear here once this event is published.
      </ErrorState>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-[1280px] space-y-4 p-4">
      <div>
        <SectionLabel>Orders</SectionLabel>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 rounded-sm border p-4 md:flex-row md:items-center md:justify-between"
        >
          <fieldset className="grid w-full grid-cols-2 gap-4 md:flex md:min-w-0 md:flex-1 md:items-center">
            <Input
              name="search"
              label="Search Term"
              value={form.search}
              onChange={(event) =>
                updateForm("search", event.target.value)
              }
              className="col-span-2 w-full md:min-w-0 md:max-w-96 md:flex-1"
            />

            <Dropdown
              name="status"
              label="Status"
              value={form.status}
              onChange={(event) =>
                updateForm("status", event.target.value)
              }
              className="w-full md:w-auto md:shrink-0"
              options={[
                { value: "all", label: "All" },
                { value: "paid", label: "Paid" },
                { value: "pending", label: "Pending" },
                { value: "cancelled", label: "Cancelled" },
                { value: "refunded", label: "Refunded" },
              ]}
            />

            <Dropdown
              name="sort"
              label="Sort By"
              value={form.sort}
              onChange={(event) =>
                updateForm("sort", event.target.value)
              }
              className="w-full md:w-auto md:shrink-0"
              options={[
                { value: "desc", label: "Latest first" },
                { value: "asc", label: "Oldest first" },
              ]}
            />
          </fieldset>

          <div className="grid w-full grid-cols-2 gap-2 md:flex md:w-fit md:shrink-0">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClear}
              className="w-full text-sm md:w-auto"
            >
              Clear Results
            </Button>

            <Button
              type="submit"
              className="w-full text-sm md:w-auto"
            >
              Search
            </Button>
          </div>
        </form>
      </div>

      <div
        ref={tableRef}
        className="overflow-x-hidden rounded-sm border px-4 py-2 md:py-4"
      >
        <table className="w-full">
          <thead className="hidden md:table-header-group">
            <tr>
              <th
                scope="col"
                className="py-2 text-left text-xs font-normal text-gray-500"
              >
                Customer
              </th>

              <th
                scope="col"
                className="py-2 text-left text-xs font-normal text-gray-500"
              >
                Email
              </th>

              <th
                scope="col"
                className="py-2 text-left text-xs font-normal text-gray-500"
              >
                Date and Time
              </th>

              <th
                scope="col"
                className="py-2 text-left text-xs font-normal text-gray-500"
              >
                Total
              </th>

              <th
                scope="col"
                className="py-2 text-left text-xs font-normal text-gray-500"
              >
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {isLoadingFirstPage && (
              <tr>
                <td
                  colSpan="5"
                  className="py-8 text-center text-sm text-gray-500"
                >
                  Loading orders...
                </td>
              </tr>
            )}

            {showEmptyState && (
              <tr>
                <td
                  colSpan="5"
                  className="py-8 text-center text-sm text-gray-500"
                >
                  No orders found.
                </td>
              </tr>
            )}

            {orders.map((order) => (
              <OrderRow
                eventId={activeEvent.id}
                key={order.id}
                order={order}
              />
            ))}
          </tbody>
        </table>

        {ordersQuery.isFetching && page > 1 && (
          <div className="p-2 text-center text-sm text-gray-500">
            Loading more orders...
          </div>
        )}

        {!hasMore && orders.length > 0 && (
          <div className="p-2 text-center text-sm text-gray-500">
            You've reached the end.
          </div>
        )}
      </div>
    </div>
  );
}